import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import AxiosClient from "../../assets/AxiosClient";
import { useStateContext } from "../../Contexts/ContextProvider";

const Attendant = () => {
  const { SetNotification, API } = useStateContext();
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

  const [attendance, setAttendance] = useState(null);

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [resending, setResending] = useState(false);
  const [activating, setActivating] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await AxiosClient.get(`/Registeration/show/${id}`);
        if (res.status === 200) {
          setUser(res.data.user);
          setAttendance(res.data.lastAttend);
        }
      } catch (err) {
        console.error("Error fetching attendant:", err);
        SetNotification("Failed to load attendant data", "danger");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDownload = async () => {
    if (!user) return;
    setDownloading(true);
    try {
      const res = await AxiosClient.get(`/Tools/get-invitation-pdf/${user.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${user.first_name}_${user.last_name}-Invite.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
      SetNotification("Failed to download invitation", "danger");
    } finally {
      setDownloading(false);
    }
  };

  const handleResend = async () => {
    if (!user) return;
    setResending(true);
    try {
      await AxiosClient.get(`/Tools/resend-accept-mail/${user.id}`);
      SetNotification("Email resent successfully", "success");
    } catch (err) {
      console.error("Resend failed:", err);
      SetNotification("Failed to resend email", "danger");
    } finally {
      setResending(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const res = await AxiosClient.put(`/Users/activation/${id}`, {
        active: !user.active,
      });
      setUser(res.data.user);
      SetNotification(
        user.active ? "User deactivated" : "User activated",
        user.active ? "warning" : "success"
      );
    } catch (err) {
      SetNotification("Activation failed", "danger");
    } finally {
      setActivating(false);
      setShowConfirmModal(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this request?"
    );
    if (!confirmed) return;

    try {
      const res = await AxiosClient.delete(`/Registeration/${id}`);
      if (res.status === 200) {
        SetNotification("Request deleted", "warning");
        navigate("/Requests");
      }
    } catch (err) {
      SetNotification("Delete failed", "danger");
    }
  };

  // Nice Yes / No badge helper
  const BoolBadge = ({ value, label }) => (
    <div className="d-flex align-items-center gap-2">
      <span className="fw-semibold text-dark">{label}:</span>
      {value ? (
        <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
          <i className="bi bi-check-circle-fill me-1"></i> Yes
        </span>
      ) : (
        <span className="badge rounded-pill bg-secondary-subtle text-secondary px-3 py-2">
          <i className="bi bi-x-circle-fill me-1"></i> No
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="alert alert-warning m-4">Attendant not found.</div>
    );
  }

  const isInPerson = attendance?.present === "In Person";

  return (
    <>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <h2 className="fw-bold mb-1">
              {user.first_name} {user.last_name}
            </h2>
            <p className="text-muted mb-0">{user.email}</p>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-outline-secondary rounded-pill"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            <Link to="./Edite" className="btn btn-outline-primary rounded-pill">
              Edit
            </Link>
          </div>
        </div>

        <div className="row g-4">
          {/* Profile card */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm rounded-3 overflow-hidden h-100">
              <div
                className={`text-center py-3 ${
                  isInPerson ? "bg-dark" : "bg-info"
                }`}
              >
                <span className="badge bg-white text-dark fw-semibold px-3 py-2">
                  {attendance?.present || "—"}
                </span>
              </div>

              <div className="card-body text-center pt-4">
                <img
                  src={`${API}${user.image}`}
                  alt="Profile"
                  className="rounded-circle border border-3 border-light shadow-sm mb-3"
                  style={{ width: 140, height: 140, objectFit: "cover" }}
                />

                <h5 className="fw-semibold mb-1">
                  {user.first_name} {user.last_name}
                </h5>
                <p className="text-muted small mb-3">{user.role}</p>

                <div className="d-flex justify-content-center gap-2 flex-wrap">
                  <span
                    className={`badge rounded-pill ${
                      user.active ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {user.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details card */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-3 h-100">
              <div className="card-body p-4">
                <h5 className="fw-semibold mb-4">Attendant Details</h5>

                <div className="row g-3">
                  {[
                    { label: "Position", value: user.position },
                    { label: "Gender", value: user.gender },
                    {
                      label: "Address",
                      value: `${user.country || ""} ${user.city || ""}`.trim(),
                    },
                    { label: "Organization", value: user.organization },
                    { label: "Nationality", value: user.nationality },
                    { label: "Phone", value: user.phone },
                    { label: "Role", value: user.role },
                  ].map((item) => (
                    <div className="col-md-6" key={item.label}>
                      <p className="text-muted small mb-1">{item.label}</p>
                      <p className="fw-medium mb-0">{item.value || "—"}</p>
                    </div>
                  ))}

 
                                  {/* ===== Attendance Requirements (Transportation & Accommodation) ===== */}
                  <div className="d-flex flex-column gap-3">
                    <BoolBadge
                      value={!!attendance?.transportation}
                      label="Transportation"
                    />
                    <BoolBadge
                      value={!!attendance?.accommodation}
                      label="Accommodation"
                    />
                  </div>
                </div>


              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="card border-0 shadow-sm rounded-3 mt-4">
          <div className="card-body p-4">
            <h5 className="fw-semibold mb-3">Actions</h5>

            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-outline-dark rounded-pill"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? "Downloading..." : "Download Invite PDF"}
              </button>

              <button
                className="btn btn-outline-warning rounded-pill"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? "Sending..." : "Resend Email"}
              </button>

              <button
                className={`btn rounded-pill ${
                  user.active ? "btn-outline-danger" : "btn-outline-success"
                }`}
                onClick={() => {
                  setActionType(user.active ? "deactivate" : "activate");
                  setShowConfirmModal(true);
                }}
                disabled={activating}
              >
                {activating
                  ? "Processing..."
                  : user.active
                  ? "Deactivate"
                  : "Activate"}
              </button>

              <button
                className="btn btn-danger rounded-pill ms-auto"
                onClick={handleDelete}
              >
                Delete Attendant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionType === "activate"
            ? "Are you sure you want to activate this user?"
            : "Are you sure you want to deactivate this user?"}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            className="rounded-pill"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded-pill"
            onClick={handleActivate}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Attendant;